import apiClient from "./apiClient";
import {config} from "../config";
import {useQuery} from "@tanstack/react-query";
import {useAppContext} from "../libs/appContextLib";
import {useMemo} from "react";

export const normalizeExistingScenarioIds = (scenarioIdsOrId) => {
    if (!scenarioIdsOrId) return [];
    if (Array.isArray(scenarioIdsOrId)) {
        return [...new Set(scenarioIdsOrId.filter(Boolean))].sort();
    }
    return [scenarioIdsOrId];
};

export const buildExistingScenariosQueryKey = (accountKey, scenarioIds = []) => (
    scenarioIds.length > 0
        ? [`${accountKey}_existing-scenarios`, scenarioIds]
        : [`${accountKey}_existing-scenarios`]
);

export const fetchExistingScenarios = async ({ scenarioIds = [], accountIdOverride = null } = {}) => {
    const params = {};
    if (scenarioIds.length > 0) {
        params.scenario_ids = JSON.stringify(scenarioIds);
    }
    const response = await apiClient.get(`${config.REACT_APP_BACKEND_HOST}/scenarios`, {
        params,
        headers: accountIdOverride ? { 'X-QFlow-Account-Id': accountIdOverride } : undefined,
    });
    return response.data;
};

export const useFetchExistingScenarios = (scenarioIdsOrId, { enabled = true, accountIdOverride = null } = {}) => {
    const {currUser} = useAppContext();
    const accountKey = accountIdOverride || currUser?.customerId;
    const scenarioIds = useMemo(() => normalizeExistingScenarioIds(scenarioIdsOrId), [scenarioIdsOrId]);

    return useQuery({
        queryKey: buildExistingScenariosQueryKey(accountKey, scenarioIds),
        queryFn: () => fetchExistingScenarios({ scenarioIds, accountIdOverride }),
        // Scenario IDs can be deleted or replaced independently of this tab.
        // Always validate persisted browser cache against the current account
        // before Board Stats submits a forecast request.
        staleTime: 1000 * 60,
        gcTime: 1000 * 60 * 60 * 36, // 36 hour -- or until invalidated
        refetchOnMount: "always",
        refetchOnWindowFocus: false,
        enabled: !!currUser?.authToken && !!accountKey && enabled
    });
}
